import React, { useMemo, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentUser } from '../../../hooks/useCurrentUser'
import useApi from '../../../hooks/useApi'
import { downloadCSV } from '../../../lib/files'
import { isEventAdmin } from '../../../lib/event'
import moment from 'moment-timezone'
import Icon from '../../../components/icon'
import Button from '../../../components/button'
import VolunteerRow from './VolunteerRow'
import styles from './volunteerList.module.scss'

export default function TeamSidebar({ event }) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { volunteerGet, getUserFollowing } = useApi()
  const [followingIds, setFollowingIds] = useState(new Set())
  const hasFetchedFollowingRef = useRef(false)
  const resetTimerRef = useRef(null)

  useEffect(() => {
    if (currentUser?.id && !hasFetchedFollowingRef.current) {
      hasFetchedFollowingRef.current = true

      const fetchFollowing = async () => {
        try {
          const response = await getUserFollowing({ id: currentUser.id })
          if (response?.data) {
            const ids = new Set(response.data.map(user => user.id))
            setFollowingIds(ids)
          }
        } catch (error) {
        } finally {
          resetTimerRef.current = setTimeout(() => {
            hasFetchedFollowingRef.current = false
            resetTimerRef.current = null
          }, 5000)
        }
      }

      fetchFollowing()
    }

    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = null
      }
    }
  }, [currentUser?.id, getUserFollowing])

  const canManage = isEventAdmin(event, currentUser)

  const organizers = useMemo(() => {
    const orgs = []
    const seenIds = new Set()

    if (event.user) {
      orgs.push({
        user: event.user,
        role: 'Organizer',
        id: event.user.id,
      })
      seenIds.add(event.user.id)
    }
    
    if (event.admins && Array.isArray(event.admins)) {
      event.admins.forEach(admin => {
        if (!seenIds.has(admin.id)) {
          orgs.push({
            user: admin,
            role: 'Admin',
            id: admin.id,
          })
          seenIds.add(admin.id)
        }
      })
    }
    
    if (event.tasks && Array.isArray(event.tasks)) {
      event.tasks.forEach(task => {
        if (task.task_leader_users && Array.isArray(task.task_leader_users)) {
          task.task_leader_users.forEach(leader => {
            if (!seenIds.has(leader.id)) {
              orgs.push({
                user: leader,
                role: 'Task Leader',
                id: leader.id,
              })
              seenIds.add(leader.id)
            }
          })
        }
      })
    }
    
    return orgs
  }, [event.user, event.admins, event.tasks])

  const handleDownloadCSV = async () => {
    if (!canManage) return

    try {
      const volunteersResponse = await volunteerGet({ id: event.id })
      const volunteers = volunteersResponse?.data || []

      const allPeople = [...organizers, ...volunteers]

      const headers = [
        'Name',
        'Role',
        'Status',
        'Check In Time',
        'Check Out Time',
        'Signed Up At',
      ]

      const rows = allPeople.map(v => {
        const status = v.check_out_time
          ? 'Checked Out'
          : v.check_in_time
            ? 'Checked In'
            : v.role || 'Signed Up'

        return [
          v.user?.name || v.name || '',
          v.role || '',
          status,
          v.check_in_time
            ? moment(v.check_in_time).format('YYYY-MM-DD HH:mm:ss')
            : '',
          v.check_out_time
            ? moment(v.check_out_time).format('YYYY-MM-DD HH:mm:ss')
            : '',
          v.created_at
            ? moment(v.created_at).format('YYYY-MM-DD HH:mm:ss')
            : '',
        ]
      })

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
      ].join('\n')

      const fileName = `volunteer_report_${event.slug || event.id}_${moment().format(
        'YYYY-MM-DD',
      )}.csv`

      downloadCSV(csvContent, fileName)
    } catch (error) {
      console.error('Error downloading CSV:', error)
    }
  }

  if (organizers.length === 0) {
    return null
  }

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <h5 className={styles.sidebarTitle}>
          {t('event.team_heading', 'Team')}
        </h5>
        <Icon name="users" className={styles.sectionIcon} />
      </div>

      <div className={styles.previewList}>
        {organizers.map((org, index) => {
          const enrichedVolunteer = {
            id: org.id,
            user: {
              ...org.user,
              current_user_follows: org.user?.current_user_follows !== undefined
                ? org.user.current_user_follows
                : followingIds.has(org.user?.id),
            },
            role: org.role,
            created_at: null,
          }
          return (
            <VolunteerRow
              key={org.id || `org-${index}`}
              volunteer={enrichedVolunteer}
              event={event}
              isOrganizer={org.role === 'Organizer'}
              isAdmin={org.role === 'Admin'}
              onUpdate={() => {}}
              showCheckInButton={false}
            />
          )
        })}
      </div>

      {canManage && (
        <Button
          className={styles.csvButton}
          onClick={handleDownloadCSV}
          variant="primary"
        >
          <Icon name="file-csv" className="me-2" />
          {t('VolunteerListScreen.downloadAlertTitle', 'Sign Up Report')}
        </Button>
      )}
    </div>
  )
}

